import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fallbackLocalesFor, offeredLocales, localeDescriptor,
  applyDocumentLocale, resolveInitialLocale, storeLocale, normalizeLocale,
} from './config';
import { loadCatalog, peekCatalog, preloadCatalog } from './loader';
import { createTranslator } from './translator';
import { createFormatters } from './format';

// ─────────────────────────────────────────────────────────────────────────────
// The one i18n context. Everything a screen needs — `t`, the formatters, the
// direction, the way to change language — comes from here and nowhere else.
//
// It is deliberately shaped like the design system's ThemeProvider, because it
// solves the same problem and should be read the same way:
//
//   1. localStorage, read synchronously during the first render, so a reload
//      paints the right language AND the right direction immediately instead of
//      flashing Persian at an English-speaking user.
//   2. The server copy (the user's `locale` attribute in Keycloak), pushed in
//      once `me` resolves. That is the real record and wins whenever it differs
//      — it is how the choice follows a staff member to the second desk.
//
// Switching language NEVER unmounts anything. The new catalogue is loaded, then
// swapped in one state update, so the router, every open dialog and every
// half-filled form survive the switch — requirement 7, and the reason this is a
// context value rather than a remount with a `key`.
// ─────────────────────────────────────────────────────────────────────────────

const I18nContext = createContext(null);

export { I18nContext };

export function I18nProvider({ language, children, fallback = null }) {
  // Lazy initialiser — runs before first paint, not in an effect, so <html dir>
  // is correct for the very first frame.
  const [locale, setLocale] = useState(() => applyDocumentLocale(resolveInitialLocale()));

  // The catalogues currently being rendered. `null` until the first load lands;
  // on a LATER switch the previous catalogue keeps rendering until the new one
  // is ready, so there is no flash of untranslated keys.
  // Where a missing key is read, nearest first (config.js `fallbackLocalesFor`):
  // Persian — unless the brand does not offer Persian — and for French and
  // German, English before it. Joined into a string so the effect below re-runs only when the
  // chain really changes, not on every render's new array.
  const chainKey = fallbackLocalesFor(locale).join(',');

  const [catalogs, setCatalogs] = useState(() => {
    const active = peekCatalog(locale);
    if (!active) return null;
    const hops = fallbackLocalesFor(locale).map((id) => ({ locale: id, catalog: peekCatalog(id) }));
    return hops.every((hop) => hop.catalog) ? { locale, active, hops } : null;
  });

  // Guards against an out-of-order resolution when the user switches twice
  // quickly: only the most recently requested locale may commit.
  const requested = useRef(locale);

  useEffect(() => {
    requested.current = locale;
    let live = true;

    // The fallback catalogues are loaded alongside the active one so a key
    // missing from a translation can resolve synchronously, inside `t`, rather
    // than triggering a load mid-render.
    const chain = chainKey ? chainKey.split(',') : [];
    Promise.all([loadCatalog(locale), ...chain.map((id) => loadCatalog(id))]).then(([active, ...rest]) => {
      if (!live || requested.current !== locale) return;
      setCatalogs({ locale, active, hops: chain.map((id, i) => ({ locale: id, catalog: rest[i] })) });
    });

    return () => { live = false; };
  }, [locale, chainKey]);

  // Adopt the server's copy when it arrives or changes (e.g. the user changed
  // language on another device). `null`/`undefined` means "not loaded yet" —
  // NOT "reset to default" — so an in-flight `me` query never clobbers a choice
  // the user just made. An unrecognised tag is ignored rather than applied:
  // a language this build does not ship must not blank the UI.
  const serverLanguage = normalizeLocale(language);
  useEffect(() => {
    if (!serverLanguage) return;
    setLocale((prev) => {
      if (prev === serverLanguage) return prev;
      storeLocale(serverLanguage);
      return applyDocumentLocale(serverLanguage);
    });
  }, [serverLanguage]);

  /**
   * Apply a language locally and cache it. Two callers:
   *   - the settings picker, so the UI switches under the user's cursor
   *     instead of after a server round trip;
   *   - app boot, to adopt the server's copy once `myProfile` resolves.
   * Persisting to the BFF is the caller's job — this only paints and caches.
   */
  const changeLocale = useCallback((next) => {
    const id = normalizeLocale(next);
    if (!id) return;
    setLocale((prev) => {
      if (prev === id) return prev;
      storeLocale(id);
      return applyDocumentLocale(id);
    });
  }, []);

  const value = useMemo(() => {
    const descriptor = localeDescriptor(locale);
    const formatters = createFormatters(locale);

    // Catalogues for a DIFFERENT locale than `locale` means a switch is in
    // flight: keep translating with what we have. `t` still resolves — it just
    // resolves in the outgoing language for the frame or two it takes the new
    // catalogue to arrive from cache or network.
    const active = catalogs?.active || {};
    const renderedLocale = catalogs?.locale || locale;
    // The LAST hop is FALLBACK_LOCALE's catalogue; any before it are the
    // locale's own nearer ones. A locale with no hop is its own fallback.
    const hops = catalogs?.hops || [];
    const last = hops[hops.length - 1];

    const pluralRules = typeof Intl.PluralRules === 'function'
      ? new Intl.PluralRules(localeDescriptor(renderedLocale).intl)
      : null;

    const { t, tList, has } = createTranslator({
      locale: renderedLocale,
      catalog: active,
      fallbackCatalog: last ? last.catalog : active,
      fallbackLocale: last ? last.locale : renderedLocale,
      bridges: hops.slice(0, -1),
      pluralRules,
      joinList: formatters.formatList,
      formatNumber: formatters.formatNumber,
    });

    return {
      // identity
      locale,
      dir: descriptor.dir,
      isRTL: descriptor.dir === 'rtl',
      // Whether this reader can read the Persian clinical reference data. NOT
      // implied by `isRTL` — Arabic shares the direction and not the language.
      readsPersianData: descriptor.readsPersianData === true,
      locales: offeredLocales(),
      // translation
      t, tList, has,
      // formatting
      ...formatters,
      // control
      setLocale: changeLocale,
      preloadLocale: preloadCatalog,
      // true once the active locale's catalogue is the one being rendered
      ready: catalogs?.locale === locale,
    };
  }, [locale, catalogs, changeLocale]);

  // Only the FIRST load blocks — after that `catalogs` is never null again, so
  // a language switch re-renders in place and the navigation state survives.
  if (!catalogs) return fallback;

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export default I18nProvider;
